class CustomProductShowcase {
  constructor(sectionId, sectionElement) {
    this.section = sectionElement || document.querySelector(`#custom-product-showcase-${sectionId}`);
    if (!this.section || this.section.dataset.productShowcaseInitialized === 'true') return;

    this.section.dataset.productShowcaseInitialized = 'true';
    this.sectionId = sectionId || this.section.dataset.sectionId;
    this.currentSlide = 0;
    this.resizeTicking = false;

    this.handleResize = this.handleResize.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);

    this.init();
  }

  init() {
    this.mainSlider = this.section.querySelector('.main-media-slider');
    this.prevBtn = this.section.querySelector('.slider-arrow--prev');
    this.nextBtn = this.section.querySelector('.slider-arrow--next');
    this.thumbnailsPrevBtn = this.section.querySelector('.thumbnail-arrow--prev');
    this.thumbnailsNextBtn = this.section.querySelector('.thumbnail-arrow--next');
    this.thumbnails = Array.from(this.section.querySelectorAll('.thumbnail-item'));
    this.slides = Array.from(this.section.querySelectorAll('.main-media-item'));
    this.thumbnailWrapper = this.section.querySelector('.thumbnail-wrapper');

    this.totalSlides = this.slides.length;
    // Browsable slides = the listing images (those with a thumbnail). Variant
    // renders live in the slider after these but have no thumbnail, so the
    // prev/next arrows must not walk into them; the colour picker still can.
    this.browseCount = this.thumbnails.length || this.totalSlides;

    const initialIndex = parseInt(
      this.section.dataset.initialSlideIndex ||
        (this.mainSlider && this.mainSlider.dataset.initialIndex) ||
        '0',
      10
    );

    this.currentSlide = Number.isNaN(initialIndex)
      ? 0
      : Math.max(0, Math.min(initialIndex, Math.max(this.totalSlides - 1, 0)));

    if (this.mainSlider && this.currentSlide > 0) {
      this.mainSlider.style.transform = `translate3d(-${this.currentSlide * 100}%, 0, 0)`;
    }

    this.initSlider();
    this.initQuantitySelector();
    this.initThumbnailClick();
    this.initVarientClick();
    this.initAccordion();
    this.initSubAccordion();
    this.initResize();

    // 产品介绍视频：首屏不抢资源，用户看到时自动播放
    this.initLazyFeatureVideos();

    this.loadSlideImage(this.currentSlide);
    this.pauseInactiveMedia();

    if (this.totalSlides <= 1) {
      if (this.prevBtn) this.prevBtn.hidden = true;
      if (this.nextBtn) this.nextBtn.hidden = true;
      if (this.thumbnailsPrevBtn) this.thumbnailsPrevBtn.hidden = true;
      if (this.thumbnailsNextBtn) this.thumbnailsNextBtn.hidden = true;
    }
  }

  initAccordion() {
    this.accordionItems = Array.from(this.section.querySelectorAll('.accordion-item'));
    if (!this.accordionItems.length) return;

    this.accordionItems.forEach((item) => {
      const header = item.querySelector('.accordion-header');
      if (!header || header.dataset.bound === 'true') return;

      header.dataset.bound = 'true';
      header.addEventListener('click', () => this.toggleItem(item));
    });
  }

  initSubAccordion() {
    this.section.querySelectorAll('.faq-header').forEach((header) => {
      if (header.dataset.bound === 'true') return;

      header.dataset.bound = 'true';

      header.addEventListener('click', () => {
        const faqItem = header.closest('.faq-item');
        if (faqItem) faqItem.classList.toggle('active');
      });
    });

    this.section.querySelectorAll('.faq-child-header').forEach((header) => {
      if (header.dataset.bound === 'true') return;

      header.dataset.bound = 'true';

      header.addEventListener('click', (e) => {
        const childItem = header.closest('.faq-child-item');
        const answer = childItem ? childItem.querySelector('.faq-child-answer') : null;

        if (!childItem || !answer) return;

        const isActive = childItem.classList.contains('active');

        if (isActive) {
          answer.style.height = `${answer.scrollHeight}px`;

          requestAnimationFrame(() => {
            answer.style.height = '0px';
          });

          childItem.classList.remove('active');
        } else {
          answer.style.height = `${answer.scrollHeight}px`;
          childItem.classList.add('active');

          answer.addEventListener('transitionend', function handler() {
            answer.style.height = 'auto';
            answer.removeEventListener('transitionend', handler);
          });
        }

        e.stopPropagation();
      });
    });
  }

  toggleItem(clickedItem) {
    const isCurrentlyActive = clickedItem.classList.contains('active');

    this.accordionItems.forEach((item) => {
      item.classList.remove('active');
    });

    if (!isCurrentlyActive) {
      clickedItem.classList.add('active');
    }
  }

  initResize() {
    window.addEventListener('resize', this.handleResize, { passive: true });
  }

  initSlider() {
    if (!this.mainSlider) return;

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.goToAdjacent(-1);
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.goToAdjacent(1);
      });
    }

    this.section.addEventListener('keydown', this.handleKeydown);
  }

  handleKeydown(e) {
    const tagName = e.target && e.target.tagName;

    if (tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT') return;

    if (e.key === 'ArrowLeft') {
      this.goToAdjacent(-1);
    } else if (e.key === 'ArrowRight') {
      this.goToAdjacent(1);
    }
  }

  goToSlide(index) {
    if (!this.mainSlider || !this.totalSlides) return;

    if (index < 0) index = this.totalSlides - 1;
    if (index >= this.totalSlides) index = 0;

    this.currentSlide = index;

    this.loadSlideImage(index);

    this.mainSlider.style.transform = `translate3d(-${index * 100}%, 0, 0)`;

    this.thumbnails.forEach((thumb, i) => {
      const active = i === index;
      thumb.classList.toggle('active', active);

      if (active && this.thumbnailWrapper) {
        thumb.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    });

    this.pauseInactiveMedia();
    this.loadAdjacentSlideImages(index);
  }

  // Prev/next browsing, capped to the listing slides. The variant renders sit
  // after the listing images (indices >= browseCount) and are reachable only
  // via goToSlideById (the colour picker) — never via the arrows/keyboard.
  goToAdjacent(direction) {
    const count = this.browseCount || this.totalSlides;
    if (!count) return;

    let base = this.currentSlide;
    // If we're currently on a variant render, re-enter the listing range.
    if (base >= count) base = direction > 0 ? -1 : count;

    let next = base + direction;
    if (next < 0) next = count - 1;
    if (next >= count) next = 0;

    this.goToSlide(next);
  }

  loadSlideImage(index) {
    const slide = this.slides[index];
    if (!slide) return;

    const img = slide.querySelector('.js-deferred-main-image');
    if (!img || img.dataset.loaded === 'true') return;

    if (img.dataset.src) img.src = img.dataset.src;
    if (img.dataset.srcset) img.srcset = img.dataset.srcset;

    img.dataset.loaded = 'true';
  }

  loadAdjacentSlideImages(index) {
    const nextIndex = index + 1 >= this.totalSlides ? 0 : index + 1;

    const preload = () => {
      this.loadSlideImage(nextIndex);
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(preload, { timeout: 8000 });
    } else {
      setTimeout(preload, 8000);
    }
  }

  pauseInactiveMedia() {
    this.slides.forEach((slide, index) => {
      if (index === this.currentSlide) return;

      slide.querySelectorAll('video').forEach((video) => {
        if (!video.paused) video.pause();
      });
    });
  }

  handleResize() {
    if (this.resizeTicking) return;

    this.resizeTicking = true;

    requestAnimationFrame(() => {
      if (this.mainSlider && this.totalSlides > 0) {
        this.mainSlider.style.transform = `translate3d(-${this.currentSlide * 100}%, 0, 0)`;
      }

      this.resizeTicking = false;
    });
  }

  goToSlideById(targetMediaId) {
    if (!targetMediaId) return;

    const targetIndex = this.slides.findIndex((item) => {
      return item.dataset.mediaId && item.dataset.mediaId === String(targetMediaId);
    });

    if (targetIndex !== -1) {
      this.goToSlide(targetIndex);
    }
  }

  initThumbnailClick() {
    if (this.thumbnailsPrevBtn && this.thumbnailWrapper) {
      this.thumbnailsPrevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.scrollThumbnails(-1);
      });
    }

    if (this.thumbnailsNextBtn && this.thumbnailWrapper) {
      this.thumbnailsNextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.scrollThumbnails(1);
      });
    }

    if (!this.thumbnails.length) return;

    this.thumbnails.forEach((thumb, index) => {
      thumb.addEventListener('click', () => {
        this.goToSlide(index);
      });
    });
  }

  scrollThumbnails(direction) {
    if (!this.thumbnailWrapper) return;

    const scrollAmount = this.getThumbnailScrollAmount();
    const currentScroll = this.thumbnailWrapper.scrollLeft;
    const maxScroll = this.thumbnailWrapper.scrollWidth - this.thumbnailWrapper.clientWidth;

    if (direction < 0 && currentScroll <= 0) {
      this.thumbnailWrapper.scrollTo({
        left: maxScroll,
        behavior: 'smooth'
      });
      return;
    }

    if (direction > 0 && currentScroll >= maxScroll - 1) {
      this.thumbnailWrapper.scrollTo({
        left: 0,
        behavior: 'smooth'
      });
      return;
    }

    this.thumbnailWrapper.scrollBy({
      left: direction * scrollAmount,
      behavior: 'smooth'
    });
  }

  getThumbnailScrollAmount() {
    const firstThumb = this.thumbnails[0];
    if (!firstThumb) return 100;

    const thumbWidth = firstThumb.offsetWidth;
    const gap = parseFloat(getComputedStyle(firstThumb.parentElement).gap) || 16;

    return (thumbWidth + gap) * 2;
  }

  initQuantitySelector() {
    const quantityBtnMinus = this.section.querySelector('#quantity-btn-minus');
    const quantityBtnPlus = this.section.querySelector('#quantity-btn-plus');
    const quantityInput = this.section.querySelector('#quantity-input-num');

    const triggerQuantityChange = (value) => {
      if (quantityInput) quantityInput.value = value;
    };

    triggerQuantityChange(1);

    if (quantityBtnMinus) {
      quantityBtnMinus.addEventListener('click', () => {
        if (!quantityInput) return;

        const currentValue = parseInt(quantityInput.value, 10) || 1;

        if (currentValue > 1) {
          triggerQuantityChange(currentValue - 1);
        }
      });
    }

    if (quantityBtnPlus) {
      quantityBtnPlus.addEventListener('click', () => {
        if (!quantityInput) return;

        const currentValue = parseInt(quantityInput.value, 10) || 1;
        triggerQuantityChange(currentValue + 1);
      });
    }

    if (quantityInput) {
      quantityInput.addEventListener('change', (e) => {
        let value = parseInt(e.target.value, 10);

        if (Number.isNaN(value) || value < 1) {
          value = 1;
        }

        triggerQuantityChange(value);
      });

      quantityInput.addEventListener('beforeinput', (e) => {
        if (e.inputType === 'insertText' && !/^\d$/.test(e.data)) {
          e.preventDefault();
        }
      });
    }
  }

  initVarientClick() {
    const variantIcons = Array.from(this.section.querySelectorAll('.variant-icon'));
    const selectedTitleSpan = this.section.querySelector('#variant-selected-title');
    const goodInputId = this.section.querySelector('#good-input-id');
    const formIdInput = this.section.querySelector('input[name="id"]');

    if (!variantIcons.length) return;

    const triggerIdChange = (value, variantMediaId, variantAvailable) => {
      if (goodInputId) goodInputId.value = value;

      if (formIdInput) {
        formIdInput.value = value;
        this.refreshAddToCartBtn(variantAvailable);
      }

      if (variantMediaId) {
        this.goToSlideById(variantMediaId);
      }
    };

    const firstAvailableIcon = this.section.querySelector('.variant-icon:not(.variant-icon-disabled)');

    if (firstAvailableIcon && selectedTitleSpan) {
      const defaultTitle = firstAvailableIcon.getAttribute('data-variant-title');
      const variantId = firstAvailableIcon.getAttribute('data-variant-id');
      const variantMediaId = firstAvailableIcon.getAttribute('data-variant-featured_media-id');
      const variantAvailable = firstAvailableIcon.getAttribute('data-variant-available');

      selectedTitleSpan.textContent = defaultTitle;
      // Load behaviour: seed the default variant's id/price/availability + mark
      // its swatch active, but pass null for the media id so the hero image is
      // NOT swapped on load — the page opens on the primary listing image. The
      // render swaps only when a colour is actively clicked (handler below).
      triggerIdChange(variantId, null, variantAvailable);
      firstAvailableIcon.classList.add('variant-icon-active');
    }

    variantIcons.forEach((icon) => {
      icon.addEventListener('click', () => {
        const variantTitle = icon.getAttribute('data-variant-title');
        const variantId = icon.getAttribute('data-variant-id');
        const variantMediaId = icon.getAttribute('data-variant-featured_media-id');
        const variantAvailable = icon.getAttribute('data-variant-available');

        if (selectedTitleSpan) selectedTitleSpan.textContent = variantTitle;

        if (variantId) {
          triggerIdChange(variantId, variantMediaId, variantAvailable);
        }

        variantIcons.forEach((variantIcon) => {
          variantIcon.classList.remove('variant-icon-active');
        });

        icon.classList.add('variant-icon-active');
      });
    });
  }

  refreshAddToCartBtn(variantAvailable) {
    const addToCartButton = this.section.querySelector('[type="submit"]');
    if (!addToCartButton) return;

    if (variantAvailable === 'true') {
      addToCartButton.disabled = false;
      addToCartButton.classList.remove('sold-out');
      addToCartButton.textContent = 'Add to cart';
    } else {
      addToCartButton.disabled = true;
      addToCartButton.classList.add('sold-out');
      addToCartButton.textContent = 'Sold out';
    }
  }

  /* ===== Product feature videos: lazy load + autoplay ===== */
  initLazyFeatureVideos() {
    this.featureVideos = Array.from(this.section.querySelectorAll('.js-lazy-feature-video'));
    if (!this.featureVideos.length) return;

    this.featureVideoQueue = [];
    this.featureVideoQueueRunning = false;

    const isMobile = window.matchMedia('(max-width: 749px)').matches;

    this.featureVideos.forEach((video) => {
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.autoplay = true;
      video.loop = true;
      video.preload = 'none';

      video.setAttribute('muted', '');
      video.setAttribute('autoplay', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('preload', 'none');

      if (video.dataset.tapBound === 'true') return;

      video.dataset.tapBound = 'true';

      // 用户点到视频时，立即加载播放，不等队列
      video.addEventListener('click', () => {
        this.loadAndPlayFeatureVideo(video, true);
      });

      video.addEventListener(
        'touchstart',
        () => {
          this.loadAndPlayFeatureVideo(video, true);
        },
        {
          passive: true,
          once: true
        }
      );
    });

    const addToQueue = (video) => {
      if (!video || video.dataset.loaded === 'true' || video.dataset.queued === 'true') return;

      video.dataset.queued = 'true';
      this.featureVideoQueue.push(video);
      this.runFeatureVideoQueue(isMobile);
    };

    const startObserver = () => {
      if (!this.featureVideos || !this.featureVideos.length) return;

      if ('IntersectionObserver' in window) {
        this.featureVideoObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;

              addToQueue(entry.target);

              if (this.featureVideoObserver) {
                this.featureVideoObserver.unobserve(entry.target);
              }
            });
          },
          {
            // Mobile 不要太早加载，避免抢首屏资源；PC 可以提前一些
            rootMargin: isMobile ? '160px 0px' : '450px 0px',
            threshold: 0.01
          }
        );

        this.featureVideos.forEach((video) => {
          this.featureVideoObserver.observe(video);
        });
      } else {
        this.featureVideos.forEach((video) => {
          addToQueue(video);
        });
      }
    };

    const start = () => {
      // Mobile：load 后 1.2 秒开始观察
      // PC：load 后 0.3 秒开始观察
      window.setTimeout(startObserver, isMobile ? 1200 : 300);
    };

    if (document.readyState === 'complete') {
      start();
    } else {
      window.addEventListener('load', start, { once: true });
    }
  }

  runFeatureVideoQueue(isMobile) {
    if (this.featureVideoQueueRunning) return;
    if (!this.featureVideoQueue || !this.featureVideoQueue.length) return;

    this.featureVideoQueueRunning = true;

    const runNext = () => {
      const video = this.featureVideoQueue.shift();

      if (!video) {
        this.featureVideoQueueRunning = false;
        return;
      }

      this.loadAndPlayFeatureVideo(video);

      // Mobile 间隔稍微大一点，避免 3 个视频同时下载；PC 间隔短一点，观感更快
      window.setTimeout(runNext, isMobile ? 900 : 350);
    };

    runNext();
  }

  loadAndPlayFeatureVideo(video, force = false) {
    if (!video) return;

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.autoplay = true;
    video.loop = true;

    video.setAttribute('muted', '');
    video.setAttribute('autoplay', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');

    if (video.dataset.loaded === 'true') {
      if (video.paused) {
        video.play().catch(() => {});
      }

      return;
    }

    if (video.dataset.loading === 'true') return;

    video.dataset.loading = 'true';

    const sources = Array.from(video.querySelectorAll('source[data-src]'));

    if (!sources.length) {
      video.dataset.loading = 'false';
      return;
    }

    sources.forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });

    video.dataset.loaded = 'true';
    video.removeAttribute('preload');
    video.preload = 'auto';

    const play = () => {
      const playPromise = video.play();

      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          // iOS 低电量模式、浏览器策略可能阻止自动播放；
          // 用户点击时会通过 force 重新触发。
        });
      }
    };

    video.addEventListener(
      'canplay',
      () => {
        video.dataset.loading = 'false';
        play();
      },
      {
        once: true
      }
    );

    video.addEventListener(
      'loadeddata',
      () => {
        video.dataset.loading = 'false';
        play();
      },
      {
        once: true
      }
    );

    video.load();

    if (force) {
      play();
    } else {
      window.setTimeout(play, 80);
    }
  }

  destroy() {
    window.removeEventListener('resize', this.handleResize);
    this.section.removeEventListener('keydown', this.handleKeydown);

    if (this.featureVideoObserver) {
      this.featureVideoObserver.disconnect();
    }

    this.section.dataset.productShowcaseInitialized = 'false';
  }
}

function initCustomProductShowcases(root = document) {
  root.querySelectorAll('.custom-product-showcase').forEach((section) => {
    const sectionId = section.dataset.sectionId;
    new CustomProductShowcase(sectionId, section);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initCustomProductShowcases();
  });
} else {
  initCustomProductShowcases();
}

document.addEventListener('shopify:section:load', (event) => {
  initCustomProductShowcases(event.target);
});
